import { useState } from "react";
import { Link } from "react-router-dom";
import { Page } from "../components/AppShell";
import {
  Badge,
  Card,
  PanelState,
  RangeFilter,
  StatTile,
} from "../components/ui";
import { useRevoke } from "../lib/useRevoke";
import { useAuth } from "../auth/AuthContext";
import { dateTime, num, relative } from "../lib/format";
import { useApi } from "../lib/useApi";
import type { EngagementResponse } from "../lib/types";

/**
 * ── Who is getting the most out of this, and who looks odd ─────────────────
 * The leaderboard answers "which customers are generating the most
 * photorealistic views" — but the column to read across is DOWNLOADS. Forty
 * renders and no downloads is someone exploring; six renders and five
 * downloads is someone who has picked a colour and is showing it to a painter.
 */
export function EngagementPage() {
  const [days, setDays] = useState(30);
  const { data, error, loading, stale, reload } = useApi<EngagementResponse>(
    "/api/admin/analytics/engagement",
    { days },
  );
  const { canRevoke } = useAuth();
  const { revokeUser, busyId, toast } = useRevoke(reload);

  return (
    <Page
      title="Engagement"
      subtitle="Power users, and sessions worth a second look"
      actions={<RangeFilter days={days} onChange={setDays} />}
    >
      <div className={stale ? "stale" : undefined}>
        <PanelState loading={loading} error={error}>
          {data && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div className="grid grid-kpi">
                <StatTile
                  label="Live sessions"
                  value={data.sessions.live}
                  hint="not revoked, not expired"
                />
                <StatTile
                  label="Sign-ins"
                  value={data.sessions.created}
                  hint={`in the last ${days} days`}
                />
                <StatTile
                  label="Sessions ended"
                  value={data.sessions.revoked}
                  hint="logouts and revocations"
                />
                <StatTile
                  label="Flagged accounts"
                  value={data.suspicious.length}
                  hint="signed in from 3+ addresses"
                />
              </div>

              <Card
                title="Top creators"
                note="Ranked by views generated. Downloads is the column that separates browsing from deciding."
              >
                <PanelState
                  loading={false}
                  error={null}
                  empty={data.creators.length === 0}
                  emptyText="Nobody generated a view in this period."
                >
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th>Type</th>
                          <th className="num">Rooms</th>
                          <th className="num">Views</th>
                          <th className="num">Downloads</th>
                          <th>Last view</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.creators.map((c) => (
                          <tr key={c.userId}>
                            <td>
                              <Link
                                to={`/customers/${c.userId}`}
                                className="cell-strong"
                                style={{ textDecoration: "underline" }}
                              >
                                {c.user?.fullName ?? "Unnamed"}
                              </Link>
                              <div className="cell-mute mono">
                                {c.user?.phone ?? c.userId}
                              </div>
                            </td>
                            <td className="cell-mute">
                              {c.user?.profileType
                                ? c.user.profileType
                                    .replace(/_/g, " ")
                                    .toLowerCase()
                                : "—"}
                            </td>
                            <td className="num">{num(c.projects)}</td>
                            <td className="num cell-strong">
                              {num(c.renders)}
                            </td>
                            <td className="num">
                              {c.downloads > 0 ? (
                                <Badge tone="good">{num(c.downloads)}</Badge>
                              ) : (
                                <span className="cell-mute">0</span>
                              )}
                            </td>
                            <td className="cell-mute">
                              {relative(c.lastRenderAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </PanelState>
              </Card>

              <Card
                title="Sessions worth a look"
                /* Not a fraud verdict, and the wording matters — a commuter on
                   mobile data trips this, and so does anyone behind CGNAT. */
                note="Accounts signed in from an unusual number of addresses. This is a shortlist for a human, not a verdict: a commuter on mobile data trips it, and so does a shared office connection. If TRUST_PROXY is unset behind a load balancer, every session records the balancer's address and this panel stays empty."
              >
                <PanelState
                  loading={false}
                  error={null}
                  empty={data.suspicious.length === 0}
                  emptyText="Nothing unusual in this period."
                >
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th className="num">Addresses</th>
                          <th className="num">Sign-ins</th>
                          <th>Seen from</th>
                          {canRevoke && <th />}
                        </tr>
                      </thead>
                      <tbody>
                        {data.suspicious.map((s) => (
                          <tr key={s.userId}>
                            <td>
                              <Link
                                to={`/customers/${s.userId}`}
                                className="cell-strong"
                                style={{ textDecoration: "underline" }}
                              >
                                {s.user?.fullName ?? "Unnamed"}
                              </Link>
                              <div className="cell-mute mono">
                                {s.user?.phone ?? s.userId}
                              </div>
                            </td>
                            <td className="num">
                              <Badge tone={s.ips >= 5 ? "warning" : "neutral"}>
                                {num(s.ips)}
                              </Badge>
                            </td>
                            <td className="num">{num(s.sessions)}</td>
                            <td className="mono cell-mute">
                              {s.addresses.slice(0, 4).join(", ")}
                              {s.addresses.length > 4 && " …"}
                            </td>
                            {canRevoke && (
                              <td style={{ textAlign: "right" }}>
                                <button
                                  className="btn btn-sm btn-danger"
                                  disabled={busyId === s.userId}
                                  onClick={() =>
                                    void revokeUser(
                                      s.userId,
                                      s.user?.fullName ?? "this customer",
                                      "flagged on engagement page",
                                    )
                                  }
                                >
                                  {busyId === s.userId
                                    ? "Ending…"
                                    : "End all sessions"}
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </PanelState>
                <p className="card-note" style={{ marginTop: 12 }}>
                  Ending a customer's sessions signs them out of every device
                  immediately — including whoever else is holding a token. They
                  will be asked for an OTP the next time they open the app.
                  Every revocation is recorded in the{" "}
                  <Link to="/audit" style={{ textDecoration: "underline" }}>
                    audit log
                  </Link>
                  . Last seen {dateTime(data.range.to)}.
                </p>
              </Card>
            </div>
          )}
        </PanelState>
      </div>
      {toast}
    </Page>
  );
}
